import { col, type Shape } from './base.ts';
import { PHASE_RATES_STD } from './estimatedRates.ts';

export const WELLTEST_RATES_STD: Shape = [
  ...PHASE_RATES_STD.slice(0, 3),
  col('liquidRatePts', 'liquidRate', 'Well-test liquid production rate (Pts sample).', { suffix: 'LiquidRatePts' }),
];

/**
 * Well-test ratio/cut measures on `MeasuredRates_WellTest_String_Agent`, applied
 * with `prefix:'wellTest'`. VERBATIM source casing preserved: `wellTestWGR`,
 * `wellTestCGR`, `wellTestGOR` are all-caps while `wellTestGorInsitu`,
 * `wellTestGorPts` are mixed-case `Gor`; `wellTestWaterCut` carries no `Std`.
 */
export const WELLTEST_RATIOS: Shape = [
  col('totalGorStd', 'gasOilRatio', 'Well-test total gas-oil ratio at standard conditions.', { suffix: 'TotalGorStd' }),
  col(
    'waterCut',
    'fraction',
    'Well-test water cut — read only when well-test WC or test results are explicitly requested; default water cut is derived from allocated rates.',
    { suffix: 'WaterCut' },
  ),
  col('wgr', 'liquidGasRatio', 'Well-test water-gas ratio.', { suffix: 'WGR' }),
  col('cgr', 'liquidGasRatio', 'Well-test condensate-gas ratio.', { suffix: 'CGR' }),
  col('gor', 'gasOilRatio', 'Well-test gas-oil ratio.', { suffix: 'GOR' }),
  col('gorInsitu', 'gasOilRatio', 'Well-test in-situ gas-oil ratio.', { suffix: 'GorInsitu' }),
  col('gorPts', 'gasOilRatio', 'Well-test gas-oil ratio (Pts sample).', { suffix: 'GorPts' }),
  col('waterCutPts', 'fraction', 'Well-test water cut (Pts sample).', { suffix: 'WaterCutPts' }),
  col('waterCutInsitu', 'fraction', 'Well-test in-situ water cut.', { suffix: 'WaterCutInsitu' }),
];

/**
 * Well-test metadata on `MeasuredRates_WellTest_String_Agent`, applied with
 * `prefix:'wellTest'`: the test start date and the approved-allocation flag.
 */
export const WELLTEST_META: Shape = [
  col('startDate', 'unitless', 'Well-test start date.', { suffix: 'StartDate' }),
  col('approvedAllocationFlag', 'unitless', 'Whether the well test is approved for allocation.', {
    suffix: 'ApprovedAllocationFlag',
  }),
];

/**
 * Un-prefixed well-test context on `MeasuredRates_WellTest_String_Agent`: the
 * formation GOR and the line-conditions CGR/WGR. Bare source columns —
 * `suffix === canonical`, `prefix:''`.
 */
export const WELLTEST_CONTEXT: Shape = [
  col('formationGOR', 'gasOilRatio', 'Formation gas-oil ratio.'),
  col('cgrLC', 'liquidGasRatio', 'Condensate-gas ratio at line conditions.'),
  col('wgrLC', 'liquidGasRatio', 'Water-gas ratio at line conditions.'),
];

/**
 * Gas-injection compliance measures on `DataDrivenRates_String_Agent`, applied
 * with `prefix:'gi'`. The compliance suffix family (`BHPCompliance`,
 * `TargetRateCompliance`, `TechnicalRateCompliance`, `WHPCompliance`) is shared
 * across both injection phases at the source, so the canonical keeps the `gi`
 * gas-injection phase marker to stay distinct from the water-injection family
 * ({@link INJ_COMPLIANCE_WATER}) composed alongside it in the same view.
 */
